import { Tag, theme, Avatar, Space, Button, Select, Input} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';


const MiniBar = ({ value, max, accent }) => {
    const pct = Math.min(100, Math.round((value / max) * 100));
    return (
        <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: accent }}>{value?.toFixed(1)}</span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>{pct}%</span>
            </div>
            <div style={{ height: 5, borderRadius: 99, background: '#f3f4f6', overflow: 'hidden' }}>
                <div style={{
                    height: '100%',
                    width: `${pct}%`,
                    borderRadius: 99,
                    background: accent,
                    transition: 'width 0.8s cubic-bezier(.4,0,.2,1)',
                }} />
            </div>
        </div>
    );
};

// Reads a value from a record given either a plain key ('department')
// or a nested path (['user', 'department']) — matches AntD's dataIndex shape.
const getNestedValue = (record, dataIndex) => {
    if (Array.isArray(dataIndex)) {
        return dataIndex.reduce((obj, k) => (obj == null ? obj : obj[k]), record);
    }
    return record?.[dataIndex];
};

export const TableCol = (
    searchText,
    setSearchText,
    searchedColumn,
    setSearchedColumn,
    departments
) => {
    const { useToken } = theme;
    const { token: tok } = useToken();


    let searchInput;

    // dataIndex can be a string ('name') or a nested path (['user', 'department'])
    const getColumnSearchProps = (dataIndex, isSelectFilter = false) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>

                {isSelectFilter ? (
                    <Select
                        showSearch
                        placeholder={`Select ${Array?.isArray(dataIndex) ? dataIndex[dataIndex.length - 1] : dataIndex}`}
                        value={selectedKeys[0]}
                        onChange={(value) => {
                            setSelectedKeys(value ? [value] : []);
                            confirm(); // Trigger filtering as soon as the selection changes
                        }}
                        style={{ marginBottom: 8, display: 'block', width: '100%' }}
                    >
                        {departments?.length > 0 && departments?.map((dept) => (
                            <Select.Option key={dept} value={dept}>
                                {dept}
                            </Select.Option>
                        ))}
                    </Select>
                ) : (

                    <Input
                        ref={(node) => {
                            searchInput = node;
                        }}
                        placeholder={`Search ${dataIndex}`}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />

                )}

                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button onClick={() => handleReset(clearFilters, confirm)} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </Space>
            </div>
        ),

        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value, record) => {
            const fieldValue = getNestedValue(record, dataIndex);
            return fieldValue
                ? fieldValue.toString().toLowerCase().includes(value.toLowerCase())
                : false;
        },
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters, confirm) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn('');
        confirm();
    };

    return [
        {
            title: '#',
            key: 'index',
            width: 52,
            render: (_, __, i) => (
                <span style={{ color: tok.colorTextTertiary, fontWeight: 600, fontSize: 13 }}>
                    {i + 1}
                </span>
            ),
        },
        {
            title: 'Faculty',
            key: 'faculty',
            render: (_, row) => {
                const user = row.user ?? {};
                const initials = user.name
                    ? user.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
                    : '??';
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar style={{
                            background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                            fontWeight: 700, flexShrink: 0,
                        }}>
                            {initials}
                        </Avatar>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: 14, color: tok.colorText }}>
                                {user?.name}
                            </div>
                            <div style={{ fontSize: 12, color: tok.colorTextTertiary }}>
                                {user?.email}
                            </div>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Designation',
            key: 'designation',
            render: (_, row) => (
                <div>
                    <div style={{ fontSize: 13, color: tok.colorText }}>{row.user?.designation}</div>
                    <div style={{ fontSize: 11, color: tok.colorTextTertiary, textTransform: 'uppercase' }}>
                        {row?.user?.school}
                    </div>
                </div>
            ),
        },
        {
            title: 'Department',
            dataIndex: ['user', 'department'],
            key: 'department',
            ...getColumnSearchProps(['user', 'department'], true),
            render: (val) => (
                <Tag color="purple" style={{ borderRadius: 6, fontWeight: 500 }}>{val}</Tag>
            ),
        },
        {
            title: 'Teaching',
            key: 'teaching',
            width: 150,
            render: (_, row) => (
                <MiniBar value={row?.teaching_apa_point} max={400} accent="#f59e0b" />
            ),
        },
        {
            title: 'Service',
            key: 'service',
            width: 150,
            render: (_, row) => (
                <MiniBar value={row?.service_apa_point} max={400} accent="#10b981" />
            ),
        },
        {
            title: 'Research',
            key: 'research',
            width: 150,
            render: (_, row) => (
                <MiniBar value={row?.research_apa_point} max={400} accent="#3b82f6" />
            ),
        },
        {
            title: 'Final APA',
            key: 'final',
            sorter: (a, b) => (a.final_apa_point ?? 0) - (b.final_apa_point ?? 0),
            defaultSortOrder: 'descend',
            render: (_, row) => (
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                    borderRadius: 99,
                    padding: '4px 14px',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: 15,
                    letterSpacing: '-0.3px',
                }}>
                    {row?.final_apa_point?.toFixed(2)}
                </div>
            ),
        },
    ]
}